/**
 * MlbTeamContext — global team selection for the GM Dashboard.
 *
 * Persists the selected team slug in localStorage under
 * `slam-mlb-gm-team` so the choice survives refresh. Exposes both
 * the slug (`team`) and the derived `abbr` / `name` for convenience
 * via the `useMlbTeam()` hook. Throws a clear error if the hook is
 * used outside `<MlbTeamProvider>`.
 */

import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
} from 'react';
import { TEAM_BY_SLUG, slugToAbbr } from './teamCodes';

const STORAGE_KEY = 'slam-mlb-gm-team';
const DEFAULT_SLUG = 'orioles';

const MlbTeamContext = createContext(null);

function readInitialSlug() {
  if (typeof window === 'undefined') return DEFAULT_SLUG;
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored && TEAM_BY_SLUG[stored]) return stored;
  } catch (err) {
    // localStorage unavailable (private mode, etc.) — fall through.
  }
  return DEFAULT_SLUG;
}

export function MlbTeamProvider({ children }) {
  const [team, setTeamState] = useState(readInitialSlug);

  // Persist on change.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(STORAGE_KEY, team);
    } catch (err) {
      // Ignore quota / availability errors.
    }
  }, [team]);

  const setTeam = useCallback((nextSlug) => {
    if (typeof nextSlug !== 'string' || !nextSlug) return;
    if (!TEAM_BY_SLUG[nextSlug]) return; // reject unknown slug — Smithers M-5
    setTeamState(nextSlug);
  }, []);

  const value = useMemo(() => {
    const meta = TEAM_BY_SLUG[team];
    return {
      team,
      setTeam,
      abbr: meta?.abbr ?? slugToAbbr(team),
      name: meta?.name ?? team,
    };
  }, [team, setTeam]);

  return (
    <MlbTeamContext.Provider value={value}>
      {children}
    </MlbTeamContext.Provider>
  );
}

export function useMlbTeam() {
  const ctx = useContext(MlbTeamContext);
  if (!ctx) {
    throw new Error('useMlbTeam must be used within a <MlbTeamProvider>');
  }
  return ctx;
}

export default MlbTeamContext;
